import { DownOutlined, LogoutOutlined, PhoneFilled, UserOutlined } from '@ant-design/icons';
import { Avatar, Dropdown } from 'antd';
import type { MenuProps } from 'antd';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { contactDetailsFor, type ContactPlatform } from '@/config/contactDetails';
import { type SupportedLanguage, SUPPORTED_LANGUAGES } from '@/i18n';

import { BrandMark } from './BrandMark';

/**
 * The Smart Form header: BMO GAM lockup, a language menu, the "Contact us"
 * line, and — for a signed-in advisor — an initials avatar with the profile
 * menu.
 *
 * ## Two audiences, one component
 *
 * The advisor and public-user designs differ only in WHERE the contact line
 * sits and whether there is an avatar:
 *
 *   advisor — bar: lockup · language · avatar; contact on its own band below
 *   public  — bar: lockup · language · contact inline, right-aligned
 *
 * Same copy, same numbers, same breakpoint behaviour ("Same for Advisor and
 * Public user" in BMO's note), so they are one component with a `variant`
 * rather than two that drift. A public user has no session, hence no initials
 * and no profile menu — that is the whole of the difference beyond layout.
 *
 * ## The contact number is not a constant
 *
 * It depends on the workflow's PLATFORM as well as the language — IFDS for the
 * registered plans, APEX for the subscription agreements — and for IFDS the
 * French line is a different NUMBER, not a translation. See
 * `config/contactDetails.ts`.
 *
 * ## Mobile (AC 1.2)
 *
 * The hours are dropped below the breakpoint and the number is kept. Done with
 * a CSS class rather than a JS width check so it survives rotation, split-view
 * and a browser-zoomed desktop — a `window.innerWidth` read is a snapshot and
 * this is a layout rule.
 */
export type SmartFormHeaderVariant = 'advisor' | 'public' | 'internal';

export type SmartFormHeaderProps = {
  variant: SmartFormHeaderVariant;
  /** Role label shown beside the name on the `internal` variant. Originator and
   *  Approver need it because one person can hold either, and the header is the
   *  only place that says which one you are signed in as. */
  roleLabel?: string | null;
  /** Rendered to the left of the profile menu — the dev-login identity switcher
   *  on internal surfaces. Kept as a slot so this component stays unaware of it. */
  headerExtra?: ReactNode;
  /** Extra items for the profile menu, shown above Sign out.
   *
   *  Internal users reach Audit history and Archived Workflows ONLY from this
   *  menu — there is no other link to either page. Moving a surface onto this
   *  header without passing them silently strands both, which is exactly what
   *  happened to the Originator when this header was first adopted. */
  menuItems?: MenuProps['items'];
  /** Drives which contact number is shown. Unknown falls back to IFDS. */
  platform?: ContactPlatform | string | null;
  /** Signed-in user's display name, for the initials. Absent → person icon. */
  name?: string | null;
  /** Split names when we have them — advisors get these from GAMX advisor-info
   *  and they are more reliable than splitting a display name on a space. */
  firstName?: string | null;
  lastName?: string | null;
  /** Where "Back to Smart form Dashboard" goes. ADVISOR VARIANT ONLY — the
   *  internal roles never get this item, configured or not (see the profile
   *  menu). Absent → the item is DISABLED rather than hidden, because it is
   *  otherwise the only one the advisor has. */
  dashboardUrl?: string | null;
  /** Internal variant only — the advisor menu has no Sign out at all. */
  onLogout?: () => void;
  onHome?: () => void;
};

/**
 * Up to two initials.
 *
 * Prefers the split names, because an advisor's display name can arrive as
 * "DEMIR, Ugur" or carry a middle name, and splitting on the first space then
 * puts the surname in the wrong slot. Falls back to the first letters of the
 * first and last whitespace-delimited tokens, then to nothing — which renders
 * the person icon. Never "??": a placeholder that looks like a bug is worse
 * than an honest generic icon.
 */
export function initialsFrom(
  name?: string | null,
  firstName?: string | null,
  lastName?: string | null,
): string {
  const letter = (s?: string | null) => s?.trim()?.[0]?.toUpperCase() ?? '';
  const fromSplit = `${letter(firstName)}${letter(lastName)}`;
  if (fromSplit) return fromSplit;

  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return letter(parts[0]);
  return `${letter(parts[0])}${letter(parts[parts.length - 1])}`;
}

export function SmartFormHeader({
  variant,
  roleLabel,
  headerExtra,
  menuItems,
  platform,
  name,
  firstName,
  lastName,
  dashboardUrl,
  onLogout,
  onHome,
}: SmartFormHeaderProps) {
  const { t, i18n } = useTranslation();
  const language: SupportedLanguage = i18n.language.startsWith('fr') ? 'fr' : 'en';
  const contact = contactDetailsFor(platform, language);
  const initials = initialsFrom(name, firstName, lastName);

  const contactBlock = (
    <div className={`sf-header__contact sf-header__contact--${variant}`}>
      {variant === 'advisor' && <PhoneFilled className="sf-header__contact-icon" />}
      <span>
        {contact.label} <a href={`tel:${contact.phone.replace(/[^\d+]/g, '')}`}>{contact.phone}</a>
        {/* One comma-separated line on the advisor bar ("…7327, Mon to Thu – …, Fri – …"); the
            comma rides inside the hours span so mobile, which drops the hours (AC 1.2), drops it
            too and keeps the number. */}
        {variant !== 'public' &&
          contact.hours?.map((line) => (
            <span key={line} className="sf-header__hours">
              , {line}
            </span>
          ))}
      </span>
      {/* The public block stacks the hours on their own lines. */}
      {variant === 'public' &&
        contact.hours?.map((line) => (
          <span key={line} className="sf-header__hours">
            {line}
          </span>
        ))}
    </div>
  );

  const languageMenu = (
    <Dropdown
      trigger={['click']}
      placement="bottomRight"
      menu={{
        selectable: true,
        selectedKeys: [language],
        items: SUPPORTED_LANGUAGES.map((code) => ({
          key: code,
          label: code.toUpperCase(),
          onClick: () => void i18n.changeLanguage(code),
        })),
      }}
    >
      <button type="button" className="sf-header__lang" aria-label={t('a11y.languageSwitcher')}>
        {language.toUpperCase()} <DownOutlined />
      </button>
    </Dropdown>
  );

  /** Name over role, per the Figma frame. Hidden on advisor and public, which
   *  show the avatar alone — an advisor already knows who they are, and a
   *  public user has no session to describe. */
  const identityBlock =
    variant === 'internal' && (name || roleLabel) ? (
      <div className="sf-header__identity">
        {name && <span className="sf-header__identity-name">{name}</span>}
        {roleLabel && <span className="sf-header__identity-role">{roleLabel}</span>}
      </div>
    ) : null;

  const dashboardItem = {
    key: 'dashboard',
    label: t('header.backToDashboard', { defaultValue: 'Back to Smart form Dashboard' }),
    // Advisor-only, and rendered even unconfigured because it is the only item they
    // have — see the note below. Disabled says "not set up yet"; an empty dropdown
    // would just look broken.
    disabled: !dashboardUrl,
    onClick: dashboardUrl ? () => window.location.assign(dashboardUrl) : undefined,
  };

  const logoutItem = {
    key: 'logout',
    icon: <LogoutOutlined />,
    label: t('auth.signOut'),
    onClick: onLogout,
  };

  /**
   * An ISAM advisor has no Sign out, by design.
   *
   * The session they are working in is BMO's, not ours: they arrive from the
   * SmartForm dashboard already authenticated, and `POST /auth/logout` deletes the
   * BFF session and clears the cookie — ending a session the dashboard still
   * believes is live and sending them back through ISAM for the next form. So the
   * advisor's single menu item HANDS THEM BACK to that dashboard and leaves the
   * cookie alone. Their session still ends on its own terms: the absolute cap in
   * useSessionTimer (ADR-0022) is untouched by this.
   *
   * NO EXCEPTIONS, including Dev Login. This menu once carried an
   * `allowAdvisorSignOut` escape hatch so a tester could leave Eve Advisor for
   * Frank, which meant the advisor menu on a dev build was not the menu that
   * ships — the one surface where "it worked when I tested it" proves least.
   * Dev identity switching does not need it: /auth/logout clears the picked
   * identity (clearDevIdentity in LogoutPage) and lands on the picker, and
   * internal routes still carry the Switch-user chip.
   *
   * Entra staff are unaffected — an originator or approver signed in through OUR
   * BFF, so signing out here is exactly right, and they keep it.
   *
   * They do NOT get the dashboard link, configured or not. BMO's SmartForm dashboard
   * is the ADVISOR's surface: it is where an advisor's session begins and the only
   * place handing them back is a real exit. An originator or approver arrives through
   * Entra and has no business there, so the item would send internal staff to a page
   * that is not theirs. Their exits are Sign out and the surfaces in `menuItems`.
   */
  const profileItems: MenuProps['items'] =
    variant === 'advisor' ? [dashboardItem] : [...(menuItems ?? []), logoutItem];

  const profileMenu = (
    <Dropdown trigger={['click']} placement="bottomRight" menu={{ items: profileItems }}>
      <button type="button" className="sf-header__profile" aria-label={t('a11y.userMenu')}>
        <Avatar
          size={34}
          className="sf-header__avatar"
          icon={initials ? undefined : <UserOutlined />}
        >
          {initials || undefined}
        </Avatar>
        <DownOutlined />
      </button>
    </Dropdown>
  );

  return (
    <div className="sf-header">
      <div className="sf-header__bar">
        {/* One asset per language — BMO's French mark reads "Gestion mondiale
            d'actifs", so it is a different image, not translated alt text.
            SVG here and PNG in the notification emails: same artwork, but
            email clients do not render SVG (Gmail strips it) while a browser
            renders it crisply at any density for a third of the bytes.

            Below 1024px BrandMark swaps the lockup for the short BMO mark —
            phone and tablet both, see the note there. The language split does
            not follow it across: the short mark is the wordmark plus the
            medallion with no "Global Asset Management" to translate, so one
            asset serves both locales.

            Wrapped in a button ONLY when there is somewhere to go: the lockup
            navigates home, so it has to BE a control to be focusable and
            announced as actionable (WCAG 2.1.1, 4.1.2) — but with no handler
            that same markup is a focusable control announced as actionable
            that does nothing, which is worse than a plain image. The public
            customer runtime is the case with no destination: every "home" it
            could offer leads into AuthGuard and then to a staff login. */}
        {onHome ? (
          <button
            type="button"
            className="sf-header__logo"
            onClick={onHome}
            aria-label={t('a11y.goHome')}
          >
            <BrandMark tone="colour" language={language} alt="" />
          </button>
        ) : (
          <span className="sf-header__logo">
            {/* Real alt text here, unlike the linked branch above where the button's
                aria-label carries the name and the image is decorative. With no button
                this image is the only thing naming the site, so it has to speak. Matches
                AppFooter's lockup. */}
            <BrandMark tone="colour" language={language} alt="BMO Global Asset Management" />
          </span>
        )}

        <div className="sf-header__right">
          {headerExtra}
          {languageMenu}
          {identityBlock}
          {variant === 'public' ? contactBlock : profileMenu}
        </div>
      </div>

      {variant === 'advisor' && <div className="sf-header__band">{contactBlock}</div>}
    </div>
  );
}
